import React, { useState } from 'react';
import { Checkbox, Button, Tag, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { keyFields, recordName } from './sections.js';

const { Text } = Typography;

// error -> red, warning -> gold, ok -> no tag at all (see render below).
const LEVEL_COLOUR = { error: 'red', warning: 'gold' };

// I3: matches `password`, `passphrase`, any `*secret*`, `pre-shared-key`,
// and any `license-key`/`license_key`/`license key` variant — case-
// insensitive and KEY-based, not tool-based, so a Wireless flex-asset trait
// literally named `password` (a PSK), or a Licensing flex-asset trait like
// `license-key-s` (documented in extractor.py as "a credential — treat like
// a password value"), are masked exactly like a create_password item's own
// `password` param. The card above the wizard promises credential values
// are never echoed back; this is what keeps that promise true for the
// expanded raw-param view too. No exceptions to this rule.
const SENSITIVE_KEY_RE = /pass(word|phrase)|secret|pre-shared-key|license[-_ ]?key/i;
const MASKED_VALUE = '••••••••';

function displayValue(key, value) {
  if (SENSITIVE_KEY_RE.test(key)) return MASKED_VALUE;
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
}

/**
 * One record in the review list: a checkbox, the record's name, up to three
 * key fields at a glance, a flag/reason for anything validation caught, and
 * an expand toggle that reveals every raw param. Collapsed by default so a
 * section with hundreds of records stays scannable.
 *
 * F5: `create_flexible_asset` items carry their real content inside
 * `params.traits` — an opaque object that used to render as a single
 * `JSON.stringify` blob. Traits are flattened into their own dl rows here
 * (trait name -> value, masked the same as any other field) so the
 * operator can actually read what's about to be imported, and a trait the
 * matching schema (`schemas[flexibleAssetTypeId]`) marks `required` gets a
 * `[required]` marker on its label.
 */
export default function RecordRow({ item, schemas, state, checked, onToggle }) {
  const [open, setOpen] = useState(false);
  const params = item.params || {};
  const name = recordName(item);
  const isFlexAsset = item.tool === 'create_flexible_asset';
  const traits = isFlexAsset ? params.traits || {} : null;
  const traitFields = traits
    ? Object.entries(traits).filter(([, v]) => v !== null && v !== undefined)
    : [];
  const requiredTraitKeys = new Set(
    isFlexAsset
      ? (schemas?.[String(params.flexibleAssetTypeId)]?.fields || [])
          .filter((f) => f.required)
          .map((f) => f.name_key)
      : [],
  );
  // `traits` itself is excluded from the generic loop below — it's
  // rendered as its own per-key rows instead, never as one blob.
  const fields = Object.entries(params).filter(
    ([k, v]) => v !== null && v !== undefined && !(isFlexAsset && k === 'traits'),
  );

  return (
    <div
      data-testid="record-row"
      style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 0', borderBottom: '1px solid var(--odin-border)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Checkbox checked={checked} onChange={onToggle} />
        <Text strong>{name}</Text>
        <Text type="secondary">{keyFields(item, schemas).join(' · ')}</Text>
        {state.level !== 'ok' && <Tag color={LEVEL_COLOUR[state.level]}>{state.level}</Tag>}
        <Button
          aria-label="expand"
          type="text"
          size="small"
          icon={open ? <UpOutlined /> : <DownOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => setOpen((o) => !o)}
        />
      </div>

      {state.reasons.map((r) => (
        <Text key={r} type={state.level === 'error' ? 'danger' : 'warning'} role="note" style={{ paddingLeft: 24, fontSize: 12 }}>
          {r}
        </Text>
      ))}

      {open && (
        <dl style={{ paddingLeft: 24, margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 8, rowGap: 2 }}>
          {fields.map(([k, v]) => (
            <React.Fragment key={k}>
              <dt style={{ color: 'var(--odin-text-tertiary)' }}>{k}</dt>
              <dd style={{ margin: 0 }}>{displayValue(k, v)}</dd>
            </React.Fragment>
          ))}
          {traitFields.map(([k, v]) => (
            <React.Fragment key={`trait-${k}`}>
              <dt style={{ color: 'var(--odin-text-tertiary)' }}>
                {k}{requiredTraitKeys.has(k) ? ' [required]' : ''}
              </dt>
              <dd style={{ margin: 0 }}>{displayValue(k, v)}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
}
